import React, { useState, useEffect } from 'react';
import { WelcomeMessageConfig, WelcomeLogEntry, WelcomeCheckResult } from '../types';

interface TestResult {
  success: boolean;
  error?: string;
  data?: Record<string, unknown>;
}

const TYPE_OPTIONS = [
  { key: 'text', label: '텍스트' },
  { key: 'photo', label: '사진' },
  { key: 'document', label: '파일' },
  { key: 'video', label: '동영상' },
  { key: 'animation', label: 'GIF' },
];

const PARSE_MODES = ['', 'Markdown', 'MarkdownV2', 'HTML'];

const defaultWelcome: WelcomeMessageConfig = {
  enabled: false,
  type: 'text',
  message: '',
  parse_mode: '',
  media_url: '',
  media_name: '',
  buttons: [],
  adminChatId: '',
};

export default function Settings() {
  const [apiKey, setApiKey] = useState('');
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);

  // 환영 메시지 상태
  const [welcome, setWelcome] = useState<WelcomeMessageConfig>(defaultWelcome);
  const [welcomeSaved, setWelcomeSaved] = useState(false);
  const [welcomeLog, setWelcomeLog] = useState<WelcomeLogEntry[]>([]);
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState<WelcomeCheckResult | null>(null);

  useEffect(() => {
    Promise.all([
      fetch('/api/settings').then(r => r.json()),
      fetch('/api/welcome/settings').then(r => r.json()),
      fetch('/api/welcome/log').then(r => r.json()),
    ]).then(([settings, welcomeConfig, log]) => {
      setApiKey(settings.apiKey || '');
      setWelcome({ ...defaultWelcome, ...welcomeConfig });
      setWelcomeLog(Array.isArray(log) ? log : []);
      setLoading(false);
    });
  }, []);

  const handleSave = async () => {
    await fetch('/api/settings', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ apiKey }),
    });
    setSaved(true);
    setTestResult(null);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await fetch('/api/settings/test');
      const data = await res.json();
      setTestResult(data);
    } catch {
      setTestResult({ success: false, error: '연결 테스트 실패' });
    }
    setTesting(false);
  };

  const handleWelcomeSave = async () => {
    await fetch('/api/welcome/settings', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(welcome),
    });
    setWelcomeSaved(true);
    setTimeout(() => setWelcomeSaved(false), 2000);
  };

  const handleWelcomeCheck = async () => {
    setChecking(true);
    setCheckResult(null);
    try {
      const res = await fetch('/api/welcome/check', { method: 'POST' });
      const data = await res.json();
      setCheckResult(data);
      // 로그 새로고침
      const logRes = await fetch('/api/welcome/log');
      const log = await logRes.json();
      setWelcomeLog(Array.isArray(log) ? log : []);
    } catch {
      setCheckResult({ error: '체크 실패', newUsersCount: 0 });
    }
    setChecking(false);
  };

  const updateWelcome = (patch: Partial<WelcomeMessageConfig>) => {
    setWelcome(prev => ({ ...prev, ...patch }));
  };

  const addButton = () => {
    const buttons = [...(welcome.buttons || []), { text: '', url: '' }];
    updateWelcome({ buttons });
  };

  const removeButton = (i: number) => {
    const buttons = (welcome.buttons || []).filter((_, idx) => idx !== i);
    updateWelcome({ buttons });
  };

  const updateButton = (i: number, field: 'text' | 'url', value: string) => {
    const buttons = [...(welcome.buttons || [])];
    buttons[i] = { ...buttons[i], [field]: value };
    updateWelcome({ buttons });
  };

  if (loading) return <p>로딩 중...</p>;

  const isMediaType = welcome.type !== 'text';

  return (
    <div>
      {/* ── API 설정 ── */}
      <h2 style={{ marginBottom: '20px' }}>API 설정</h2>
      <div style={cardStyle}>
        <label style={labelStyle}>API Key</label>
        <input
          type="text"
          value={apiKey}
          onChange={e => setApiKey(e.target.value)}
          placeholder="예: agk_155_..."
          style={{ ...inputStyle, fontFamily: 'monospace' }}
        />
        <p style={{ fontSize: '13px', color: '#888', margin: '8px 0 16px' }}>
          발급받은 Message API Key를 입력하세요.
        </p>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={handleSave} style={btnPrimary}>저장</button>
          <button
            onClick={handleTest}
            disabled={testing || !apiKey}
            style={{ ...btnSuccess, ...(testing || !apiKey ? btnDisabled : {}) }}
          >
            {testing ? '테스트 중...' : '연결 테스트'}
          </button>
          {saved && <span style={{ color: '#4caf50', fontWeight: 600 }}>저장되었습니다!</span>}
        </div>

        {testResult && (
          <div style={{
            marginTop: '16px', padding: '12px 16px', borderRadius: '6px',
            background: testResult.success ? '#e8f5e9' : '#fef2f2',
            border: `1px solid ${testResult.success ? '#a5d6a7' : '#fecaca'}`,
          }}>
            {testResult.success ? (
              <div>
                <div style={{ color: '#2e7d32', fontWeight: 600, marginBottom: '4px' }}>연결 성공</div>
                {testResult.data && (
                  <pre style={{ fontSize: '12px', color: '#333', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                    {JSON.stringify(testResult.data, null, 2)}
                  </pre>
                )}
              </div>
            ) : (
              <div style={{ color: '#d32f2f', fontWeight: 600 }}>연결 실패: {testResult.error}</div>
            )}
          </div>
        )}
      </div>

      {/* ── 환영 메시지 설정 ── */}
      <h2 style={{ marginTop: '32px', marginBottom: '20px' }}>환영 메시지 설정</h2>
      <div style={cardStyle}>
        <p style={{ fontSize: '13px', color: '#888', margin: '0 0 16px' }}>
          신규 사용자가 감지되면 자동으로 환영 메시지를 전송합니다. (5분 간격 체크)
        </p>

        {/* 활성화 토글 */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={welcome.enabled}
              onChange={e => updateWelcome({ enabled: e.target.checked })}
              style={{ width: '18px', height: '18px' }}
            />
            <span style={{ fontWeight: 600 }}>환영 메시지 활성화</span>
          </label>
        </div>

        {/* 관리자 Chat ID */}
        <div style={fieldGroup}>
          <label style={labelStyle}>관리자 Chat ID</label>
          <input
            type="text"
            value={welcome.adminChatId || ''}
            onChange={e => updateWelcome({ adminChatId: e.target.value })}
            placeholder="예: 123456789"
            style={inputStyle}
          />
          <p style={hintStyle}>신규 사용자 감지 시 이 Chat ID로 알림이 전송됩니다.</p>
        </div>

        {/* 메시지 타입 */}
        <div style={fieldGroup}>
          <label style={labelStyle}>메시지 타입</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '8px' }}>
            {TYPE_OPTIONS.map(opt => (
              <div
                key={opt.key}
                onClick={() => updateWelcome({ type: opt.key })}
                style={{
                  padding: '10px 8px', textAlign: 'center', fontSize: '13px', cursor: 'pointer',
                  border: welcome.type === opt.key ? '2px solid #1976d2' : '1px solid #ddd',
                  borderRadius: '8px', background: welcome.type === opt.key ? '#e3f2fd' : '#fff',
                  fontWeight: welcome.type === opt.key ? 600 : 400, transition: 'all 0.15s',
                }}
              >
                {opt.label}
              </div>
            ))}
          </div>
        </div>

        {/* 미디어 URL (media 타입일 때) */}
        {isMediaType && (
          <div style={fieldGroup}>
            <label style={labelStyle}>미디어 URL</label>
            <input
              type="text"
              value={welcome.media_url || ''}
              onChange={e => updateWelcome({ media_url: e.target.value })}
              placeholder="https://example.com/image.jpg"
              style={inputStyle}
            />
            <p style={hintStyle}>영구적인 URL을 입력하세요. (tmpfiles.org 등 임시 URL은 사용 불가)</p>
          </div>
        )}

        {/* 파일명 (document 타입일 때) */}
        {welcome.type === 'document' && (
          <div style={fieldGroup}>
            <label style={labelStyle}>파일명</label>
            <input
              type="text"
              value={welcome.media_name || ''}
              onChange={e => updateWelcome({ media_name: e.target.value })}
              placeholder="document.pdf"
              style={inputStyle}
            />
          </div>
        )}

        {/* 메시지 내용 */}
        <div style={fieldGroup}>
          <label style={labelStyle}>{isMediaType ? '캡션' : '메시지'}</label>
          <textarea
            value={welcome.message}
            onChange={e => updateWelcome({ message: e.target.value })}
            placeholder={isMediaType ? '캡션을 입력하세요...' : '환영 메시지를 입력하세요...'}
            rows={4}
            style={{ ...inputStyle, resize: 'vertical' as const }}
          />
        </div>

        {/* parse_mode */}
        <div style={fieldGroup}>
          <label style={labelStyle}>포맷</label>
          <select
            value={welcome.parse_mode || ''}
            onChange={e => updateWelcome({ parse_mode: e.target.value })}
            style={{ padding: '8px 10px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '14px', background: '#fff' }}
          >
            {PARSE_MODES.map(m => (
              <option key={m} value={m}>{m || '없음'}</option>
            ))}
          </select>
        </div>

        {/* 인라인 버튼 */}
        <div style={fieldGroup}>
          <label style={labelStyle}>인라인 버튼</label>
          {(welcome.buttons || []).map((btn, i) => (
            <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
              <input
                type="text"
                value={btn.text}
                onChange={e => updateButton(i, 'text', e.target.value)}
                placeholder="버튼 텍스트"
                style={{ ...inputStyle, flex: 1 }}
              />
              <input
                type="text"
                value={btn.url}
                onChange={e => updateButton(i, 'url', e.target.value)}
                placeholder="https://..."
                style={{ ...inputStyle, flex: 1 }}
              />
              <button
                onClick={() => removeButton(i)}
                style={{ padding: '6px 12px', background: '#ef5350', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', flexShrink: 0 }}
              >
                삭제
              </button>
            </div>
          ))}
          {(welcome.buttons || []).length < 10 && (
            <button
              onClick={addButton}
              style={{ padding: '6px 16px', background: '#f5f5f5', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}
            >
              + 버튼 추가
            </button>
          )}
        </div>

        {/* 저장 + 수동 체크 */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginTop: '8px' }}>
          <button onClick={handleWelcomeSave} style={btnPrimary}>저장</button>
          <button
            onClick={handleWelcomeCheck}
            disabled={checking || !welcome.enabled}
            style={{ ...btnSuccess, ...(checking || !welcome.enabled ? btnDisabled : {}) }}
          >
            {checking ? '확인 중...' : '수동 체크'}
          </button>
          {welcomeSaved && <span style={{ color: '#4caf50', fontWeight: 600 }}>저장되었습니다!</span>}
        </div>

        {/* 체크 결과 */}
        {checkResult && (
          <div style={{
            marginTop: '16px', padding: '12px 16px', borderRadius: '6px',
            background: checkResult.error ? '#fef2f2' : checkResult.initialized ? '#fff8e1' : checkResult.newUsersCount === 0 ? '#f5f5f5' : '#e8f5e9',
            border: `1px solid ${checkResult.error ? '#fecaca' : checkResult.initialized ? '#ffe082' : checkResult.newUsersCount === 0 ? '#e0e0e0' : '#a5d6a7'}`,
          }}>
            {checkResult.error ? (
              <div style={{ color: '#d32f2f', fontWeight: 600 }}>{checkResult.error}</div>
            ) : checkResult.skipped ? (
              <div style={{ color: '#888' }}>{checkResult.reason}</div>
            ) : checkResult.initialized ? (
              <div style={{ color: '#f57f17', fontWeight: 600 }}>초기화 완료: 기존 사용자가 등록되었습니다. 다음 체크부터 신규 사용자를 감지합니다.</div>
            ) : checkResult.newUsersCount === 0 ? (
              <div style={{ color: '#666' }}>신규 사용자가 없습니다.</div>
            ) : (
              <div>
                <div style={{ color: '#2e7d32', fontWeight: 600, marginBottom: '8px' }}>
                  신규 사용자 {checkResult.newUsersCount}명 감지, 환영 메시지 전송 완료
                </div>
                {checkResult.results && (
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {checkResult.results.map((r, i) => (
                      <li key={i} style={{ padding: '4px 0', fontSize: '13px' }}>
                        <span style={{ color: r.success ? '#4caf50' : '#d32f2f' }}>
                          {r.success ? '✅' : '❌'}
                        </span>{' '}
                        User ID: {r.userId}
                        {r.error && <span style={{ color: '#d32f2f', marginLeft: '8px' }}>({r.error})</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}

        {/* 최근 전송 로그 */}
        {welcomeLog.length > 0 && (
          <div style={{ marginTop: '24px' }}>
            <label style={{ ...labelStyle, marginBottom: '8px' }}>최근 전송 로그</label>
            {welcomeLog.slice(0, 5).map((entry, i) => (
              <div key={i} style={{
                padding: '10px 14px', marginBottom: '8px', borderRadius: '6px',
                background: '#fafafa', border: '1px solid #eee', fontSize: '13px',
              }}>
                <div style={{ color: '#666', marginBottom: '4px' }}>
                  {new Date(entry.timestamp).toLocaleString('ko-KR')} — 신규 {entry.newUserIds.length}명
                </div>
                {entry.results.map((r, j) => (
                  <div key={j} style={{ marginLeft: '12px', color: r.success ? '#388e3c' : '#d32f2f' }}>
                    {r.success ? '✅' : '❌'} ID: {r.userId}{r.error ? ` (${r.error})` : ''}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── 공통 스타일 ──
const cardStyle: React.CSSProperties = {
  background: '#fff', padding: '24px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
};

const labelStyle: React.CSSProperties = {
  display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '14px',
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', border: '1px solid #ccc',
  borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box',
};

const hintStyle: React.CSSProperties = {
  fontSize: '12px', color: '#888', margin: '4px 0 0',
};

const fieldGroup: React.CSSProperties = {
  marginBottom: '16px',
};

const btnPrimary: React.CSSProperties = {
  padding: '10px 24px', background: '#1976d2', color: '#fff',
  border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px',
};

const btnSuccess: React.CSSProperties = {
  padding: '10px 24px', background: '#388e3c', color: '#fff',
  border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px',
};

const btnDisabled: React.CSSProperties = {
  background: '#ccc', cursor: 'not-allowed',
};
